import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  CheckCircle2,
  CircleAlert,
  Clock3,
  Copy,
  ExternalLink,
  Globe2,
  MailCheck,
  Plus,
  RefreshCw,
  Save,
  ServerCog,
  ShieldCheck,
} from "lucide-react";
import type {
  MailDnsRecordRequirement,
  MailDomainSetup,
  MailDomainSetupPhase,
  MailDomainSummary,
  MailGroupPolicy,
} from "../../shared/types";
import { PageHeader } from "../components/app-shell";
import { Button, Dialog, ErrorState, Field, Loading, Tag, Toggle, useToast } from "../components/ui";
import { api, jsonBody } from "../lib/api";

type GroupDraft = Pick<MailGroupPolicy,
  "name" | "description" | "public" | "vipOnly" | "defaultTtlHours" | "maxExtensions" |
  "maxAddresses" | "vipMaxAddresses" | "allowPermanent" | "allowOutbound" | "outboundDailyLimit" |
  "outboundHourlyLimit" | "retentionDays"
>;

const phaseLabels: Record<MailDomainSetupPhase, string> = {
  dns: "待配置 DNS",
  routing: "待验证路由",
  ready: "已可用",
};

function draftFromGroup(group: MailGroupPolicy): GroupDraft {
  return {
    name: group.name,
    description: group.description,
    public: group.public,
    vipOnly: group.vipOnly,
    defaultTtlHours: group.defaultTtlHours,
    maxExtensions: group.maxExtensions,
    maxAddresses: group.maxAddresses,
    vipMaxAddresses: group.vipMaxAddresses,
    allowPermanent: group.allowPermanent,
    allowOutbound: group.allowOutbound,
    outboundDailyLimit: group.outboundDailyLimit,
    outboundHourlyLimit: group.outboundHourlyLimit,
    retentionDays: group.retentionDays,
  };
}

function recordStatus(record: MailDnsRecordRequirement) {
  if (record.status === "valid") return <span className="dns-record-status dns-record-status--valid"><Check size={12} />已检测</span>;
  if (record.status === "invalid") return <span className="dns-record-status dns-record-status--invalid"><CircleAlert size={12} />未生效</span>;
  return <span className="dns-record-status"><Clock3 size={12} />待检测</span>;
}

function DnsRecordTable({
  records,
  onCopy,
}: {
  records: MailDnsRecordRequirement[];
  onCopy: (value: string) => void;
}) {
  return (
    <div className="dns-record-table-wrap">
      <table className="data-table dns-record-table">
        <thead><tr><th>类型</th><th>名称 / HOST</th><th>值 / TARGET</th><th>状态</th><th></th></tr></thead>
        <tbody>{records.map((record) => <tr key={record.key}><td data-label="类型"><Tag tone={record.type === "MX" ? "dark" : "outline"}>{record.type}</Tag></td><td data-label="名称"><code>{record.name}</code></td><td data-label="值"><div className="dns-record-value"><code title={record.value}>{record.value}</code><small>{record.note}</small></div></td><td data-label="状态">{recordStatus(record)}</td><td><button type="button" className="icon-button" disabled={record.key === "dkim"} onClick={() => onCopy(record.value)} aria-label={`复制 ${record.key} 记录`}><Copy size={13} /></button></td></tr>)}</tbody>
      </table>
    </div>
  );
}

function SetupSteps({ phase }: { phase: MailDomainSetupPhase }) {
  const current = phase === "dns" ? 2 : phase === "routing" ? 3 : 4;
  const steps = ["添加域名", "配置 DNS", "验证收信路由", "进入域名池"];
  return (
    <ol className="domain-setup-steps" aria-label="域名接入进度">
      {steps.map((label, index) => {
        const number = index + 1;
        const state = number < current || phase === "ready" ? "done" : number === current ? "current" : "pending";
        return <li key={label} className={`domain-setup-step domain-setup-step--${state}`}><span>{state === "done" ? <Check size={13} /> : `0${number}`}</span><strong>{label}</strong></li>;
      })}
    </ol>
  );
}

export function MailDomainsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<GroupDraft | null>(null);
  const [newDomain, setNewDomain] = useState("");
  const [setupDomainId, setSetupDomainId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["mail-groups"],
    queryFn: () => api<MailGroupPolicy[]>("/api/admin/mail/groups"),
  });
  const selected = useMemo(() => query.data?.find((group) => group.id === selectedId) ?? query.data?.find((group) => group.public) ?? query.data?.[0] ?? null, [query.data, selectedId]);
  const domains = useMemo(() => query.data?.flatMap((group) => group.domains) ?? [], [query.data]);
  const activeDraft = draft ?? (selected ? draftFromGroup(selected) : null);
  const setupDomain = domains.find((domain) => domain.id === setupDomainId) ?? null;

  const setupQuery = useQuery({
    queryKey: ["mail-domain-setup", setupDomainId],
    queryFn: () => api<MailDomainSetup>(`/api/admin/mail/domains/${setupDomainId}/setup`),
    enabled: Boolean(setupDomainId),
    refetchInterval: (state) => state.state.data?.phase === "routing" ? 4_000 : false,
  });
  useEffect(() => {
    if (setupQuery.data?.phase === "ready") {
      void queryClient.invalidateQueries({ queryKey: ["mail-groups"] });
    }
  }, [queryClient, setupQuery.data?.phase]);

  const saveMutation = useMutation({
    mutationFn: () => api<MailGroupPolicy>(`/api/admin/mail/groups/${selected?.id}`, { method: "PATCH", ...jsonBody(activeDraft) }),
    async onSuccess(group) {
      setDraft(draftFromGroup(group));
      await queryClient.invalidateQueries({ queryKey: ["mail-groups"] });
      toast.push("域组策略已保存");
    },
    onError(error) { toast.fromError(error); },
  });

  const updateDomainMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
      api<MailDomainSummary>(`/api/admin/mail/domains/${id}`, { method: "PATCH", ...jsonBody(input) }),
    async onSuccess() { await queryClient.invalidateQueries({ queryKey: ["mail-groups"] }); },
    onError(error) { toast.fromError(error); },
  });

  const createDomainMutation = useMutation({
    mutationFn: () => api<MailDomainSummary>("/api/admin/mail/domains", {
      method: "POST",
      ...jsonBody({ domain: newDomain, groupId: selected?.id, inboundEnabled: true, outboundEnabled: false }),
    }),
    async onSuccess(domain) {
      setNewDomain("");
      setSetupDomainId(domain.id);
      await queryClient.invalidateQueries({ queryKey: ["mail-groups"] });
      toast.push("域名已保存，请按向导完成接入");
    },
    onError(error) { toast.fromError(error); },
  });

  const dnsCheckMutation = useMutation({
    mutationFn: (id: string) => api<MailDomainSetup>(`/api/admin/mail/domains/${id}/check-dns`, { method: "POST" }),
    async onSuccess(setup) {
      queryClient.setQueryData(["mail-domain-setup", setup.domainId], setup);
      await queryClient.invalidateQueries({ queryKey: ["mail-groups"] });
      toast.push(setup.phase === "routing" ? "DNS 已通过，请完成真实收信测试" : "检测完成，请按红色提示修正记录");
    },
    onError(error) { toast.fromError(error); },
  });

  const submitNewDomain = (event: FormEvent) => {
    event.preventDefault();
    createDomainMutation.mutate();
  };

  const change = <K extends keyof GroupDraft>(key: K, value: GroupDraft[K]) => {
    const next = { ...(activeDraft as GroupDraft), [key]: value };
    if (key === "public" && value === true) next.vipOnly = false;
    if (key === "vipOnly" && value === true) next.public = false;
    setDraft(next);
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.push("已复制");
    } catch {
      toast.push("浏览器未允许复制，请手动选择", "error");
    }
  };

  return (
    <div className="page mail-domains-page">
      <PageHeader eyebrow="MAIL DOMAINS · DOMAIN GROUPS" title="邮件域名与域组" body="添加域名后按向导完成 DNS 与实际收信检测；只有验证通过的域名才会进入临时邮箱池。" />
      {query.isLoading ? <Loading label="载入邮件域组" /> : query.error ? <ErrorState error={query.error} retry={() => void query.refetch()} /> : selected && activeDraft ? (
        <section className="mail-domain-layout">
          <div className="mail-domain-catalog">
            <header><div><h2>邮件域名</h2><span className="eyebrow">MAIL DOMAINS · VERIFIED ROUTING</span></div></header>
            <div className="mail-domain-table-wrap">
              <table className="data-table mail-domain-table">
                <thead><tr><th>域名 DOMAIN</th><th>所属域组</th><th>在用地址</th><th>接入状态</th><th>收信</th><th>发信</th></tr></thead>
                <tbody>{domains.map((domain) => (
                  <tr key={domain.id}>
                    <td data-label="域名"><button type="button" className="domain-name-button" onClick={() => setSetupDomainId(domain.id)}><strong>{domain.domain}</strong><span>查看接入向导</span></button></td>
                    <td data-label="域组"><select className="table-select" value={domain.groupId} onChange={(event) => updateDomainMutation.mutate({ id: domain.id, input: { groupId: event.target.value } })}>{query.data?.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}</select></td>
                    <td data-label="在用地址">{domain.activeAddresses.toLocaleString("zh-CN")}</td>
                    <td data-label="接入状态"><button type="button" className="plain-status-button" onClick={() => setSetupDomainId(domain.id)}><Tag tone={domain.setupPhase === "ready" ? "neutral" : domain.setupPhase === "routing" ? "outline" : "accent"}>{phaseLabels[domain.setupPhase]}</Tag></button></td>
                    <td data-label="收信">{domain.setupPhase === "ready" ? <Toggle checked={domain.inboundEnabled} onChange={(value) => updateDomainMutation.mutate({ id: domain.id, input: { inboundEnabled: value } })} label={domain.inboundEnabled ? "开" : "关"} /> : <Tag tone="outline">验证后开放</Tag>}</td>
                    <td data-label="发信">{domain.setupPhase === "ready" ? <Toggle checked={domain.outboundEnabled} onChange={(value) => updateDomainMutation.mutate({ id: domain.id, input: { outboundEnabled: value } })} label={domain.outboundEnabled ? "开" : "关"} /> : <Tag tone="outline">尚未开放</Tag>}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <form className="mail-domain-add" onSubmit={submitNewDomain}>
              <Globe2 size={16} /><div><input className="input" value={newDomain} onChange={(event) => setNewDomain(event.target.value)} placeholder="example.com" autoCapitalize="none" spellCheck={false} required /><small>域名须已接入当前 Cloudflare 账户并使用 Cloudflare DNS</small></div><Button variant="primary" type="submit" disabled={createDomainMutation.isPending}><Plus size={13} />添加到「{selected.name}」</Button>
            </form>
            <div className="mail-group-cards"><span className="eyebrow">DOMAIN GROUPS · 域组</span><div>{query.data?.map((group) => <button type="button" key={group.id} className={group.id === selected.id ? "mail-group-card mail-group-card--active" : "mail-group-card"} onClick={() => { setSelectedId(group.id); setDraft(draftFromGroup(group)); }}><strong>{group.name}</strong><span>{group.domains.map((domain) => domain.domain).join(" · ") || "暂无域名"}</span><small>{group.public ? "PUBLIC" : group.vipOnly ? "VIP ONLY" : `${group.memberCount} MEMBERS`}</small></button>)}</div></div>
          </div>

          <form className="mail-group-policy" onSubmit={(event) => { event.preventDefault(); saveMutation.mutate(); }}>
            <header><div><h2>域组：{selected.name}</h2><span className="eyebrow">GROUP POLICY</span></div><p>配置该组可用的域名、地址时效与发信能力。</p></header>
            <section><span className="eyebrow">01 · 基本资料</span><div className="policy-two-col"><Field label="域组名称"><input className="input" value={activeDraft.name} onChange={(event) => change("name", event.target.value)} /></Field><Field label="说明"><input className="input" value={activeDraft.description} onChange={(event) => change("description", event.target.value)} /></Field></div><div className="policy-access-row"><Toggle checked={activeDraft.public} onChange={(value) => change("public", value)} label="公共域组（匿名访客可用）" /><Toggle checked={activeDraft.vipOnly} onChange={(value) => change("vipOnly", value)} label="VIP 专享域组" /></div></section>
            <section><span className="eyebrow">02 · 地址时效</span><div className="policy-four-col"><Field label="默认时效（小时）"><input className="input" type="number" value={activeDraft.defaultTtlHours} onChange={(event) => change("defaultTtlHours", Number(event.target.value))} /></Field><Field label="可延长次数"><input className="input" type="number" value={activeDraft.maxExtensions} onChange={(event) => change("maxExtensions", Number(event.target.value))} /></Field><Field label="普通用户上限"><input className="input" type="number" value={activeDraft.maxAddresses} onChange={(event) => change("maxAddresses", Number(event.target.value))} /></Field><Field label="VIP 同时持有上限"><input className="input" type="number" value={activeDraft.vipMaxAddresses} onChange={(event) => change("vipMaxAddresses", Number(event.target.value))} /></Field></div><div className="policy-toggle-row"><div><strong>允许「永不过期」</strong><span>开启后该组成员可将地址锁定为常驻</span></div><Toggle checked={activeDraft.allowPermanent} onChange={(value) => change("allowPermanent", value)} label={activeDraft.allowPermanent ? "开" : "关"} /></div></section>
            <section><span className="eyebrow">03 · 发信能力</span><div className="policy-toggle-row policy-toggle-row--accent"><div><strong>允许发送邮件 · OUTBOUND</strong><span>匿名访客永远只能收信；外发还要求域名另行完成 Email Sending 配置</span></div><Toggle checked={activeDraft.allowOutbound} onChange={(value) => change("allowOutbound", value)} label={activeDraft.allowOutbound ? "开" : "关"} /></div><div className="policy-three-col"><Field label="每日上限"><input className="input" type="number" value={activeDraft.outboundDailyLimit} onChange={(event) => change("outboundDailyLimit", Number(event.target.value))} /></Field><Field label="每小时限速"><input className="input" type="number" value={activeDraft.outboundHourlyLimit} onChange={(event) => change("outboundHourlyLimit", Number(event.target.value))} /></Field><Field label="邮件保留（天）"><input className="input" type="number" value={activeDraft.retentionDays} onChange={(event) => change("retentionDays", Number(event.target.value))} /></Field></div></section>
            <footer><Button variant="primary" type="submit" disabled={saveMutation.isPending}><Save size={14} />{saveMutation.isPending ? "保存中" : "保存域组策略"}</Button><span><ShieldCheck size={13} />域名开关受服务端验证状态约束，不能手动伪造 DNS 就绪。</span></footer>
          </form>
        </section>
      ) : null}

      <Dialog open={Boolean(setupDomainId)} onClose={() => setSetupDomainId(null)} title={setupDomain?.domain ?? setupQuery.data?.domain ?? "域名接入"} eyebrow="DOMAIN ONBOARDING · CLOUDFLARE" wide>
        {setupQuery.isLoading ? <Loading label="载入接入状态" /> : setupQuery.error ? <ErrorState error={setupQuery.error} retry={() => void setupQuery.refetch()} /> : setupQuery.data ? (
          <div className="domain-setup">
            <SetupSteps phase={setupQuery.data.phase} />
            <section className="domain-setup-intro">
              <div><span className="eyebrow">INBOUND EMAIL · 接收邮件</span><h3>{setupQuery.data.phase === "ready" ? "域名已进入临时邮箱池" : "先让 Cloudflare 接管入站邮件"}</h3><p>接入检测分两层：公开 DNS 记录全部正确后，再用一封真实邮件证明 Catch-all 已指向当前 Worker。</p></div>
              <Tag tone={setupQuery.data.phase === "ready" ? "neutral" : "accent"}>{phaseLabels[setupQuery.data.phase]}</Tag>
            </section>

            <section className="domain-setup-panel">
              <header><div><span className="eyebrow">STEP 02 · DNS RECORDS</span><h3>需要配置的 DNS</h3></div><a className="setup-external-link" href="https://dash.cloudflare.com" target="_blank" rel="noreferrer">打开 Cloudflare <ExternalLink size={13} /></a></header>
              {setupQuery.data.phase === "dns" ? <DnsRecordTable records={setupQuery.data.records} onCopy={(value) => void copy(value)} /> : <details className="dns-record-details"><summary><span><CheckCircle2 size={15} />6 条 DNS 记录已通过</span><span>展开查看明细</span></summary><DnsRecordTable records={setupQuery.data.records} onCopy={(value) => void copy(value)} /></details>}
              {setupQuery.data.phase === "dns" ? <div className="cloudflare-routing-guide">
                <ServerCog size={20} />
                <div><strong>Cloudflare 控制台操作</strong><ol><li>确认该域名使用 Cloudflare DNS。</li><li>进入 <b>Compute → Email Service → Email Routing</b>，选择 <b>Onboard Domain</b>，让 Cloudflare 自动添加 MX、SPF 与 DKIM。</li><li>另行添加上表的 <b>_g2al-verify</b> TXT 所有权记录。</li></ol></div>
              </div> : null}
              {setupQuery.data.lastError ? <div className="domain-setup-error" role="alert"><CircleAlert size={15} /><span>{setupQuery.data.lastError}</span></div> : null}
              {setupQuery.data.phase !== "ready" ? <footer><Button variant="primary" onClick={() => dnsCheckMutation.mutate(setupQuery.data.domainId)} disabled={dnsCheckMutation.isPending}><RefreshCw size={14} className={dnsCheckMutation.isPending ? "spin" : ""} />{dnsCheckMutation.isPending ? "检测中" : "检测 DNS"}</Button><span>DNS 通常 5–15 分钟生效，最迟可能需要 24 小时。</span></footer> : null}
            </section>

            <section className={`domain-routing-test ${setupQuery.data.phase === "dns" ? "domain-routing-test--locked" : ""}`}>
              <header><div><span className="eyebrow">STEP 03 · REAL DELIVERY</span><h3>验证 Catch-all 到 Worker</h3></div>{setupQuery.data.phase === "ready" ? <CheckCircle2 size={25} /> : <MailCheck size={25} />}</header>
              {setupQuery.data.phase === "dns" ? <p>DNS 全部通过后会生成下一步。系统不会因为手动点击而把域名标记为可用。</p> : setupQuery.data.phase === "routing" && setupQuery.data.testAddress ? <><p>在 Cloudflare 的 <b>Routing Rules</b> 中启用 <b>Catch-all</b>，Action 选择 <b>Send to a Worker</b>，Worker 选择 <code>{setupQuery.data.workerName}</code>。然后从任意真实邮箱发送一封邮件到：</p><div className="domain-test-address"><code>{setupQuery.data.testAddress}</code><Button onClick={() => void copy(setupQuery.data?.testAddress ?? "")}><Copy size={13} />复制测试地址</Button></div><footer><Button variant="primary" onClick={() => void setupQuery.refetch()}><RefreshCw size={14} />检查是否收到</Button><span><span className="live-dot" />页面每 4 秒自动检测；测试邮件只用于验证，不会存入收件箱。</span></footer></> : <div className="domain-ready-state"><CheckCircle2 size={30} /><div><strong>收信路由验证成功</strong><p>新建临时邮箱时已经可以选择 {setupQuery.data.domain}。停用收信仍可在域名列表操作。</p></div></div>}
            </section>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
